import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiCalendar, FiChevronDown, FiClock, FiDownload, FiGlobe } from "react-icons/fi";
import MainLayouts from "../../layout/MainLayouts";
import { safeGet, safeSet } from "../../utils/safeStorage";
import {
  CLASS_GROUPS,
  COHORT_NAME,
  COURSES,
  DAY_NOTES,
  EVENTS,
  OFFICE_HOURS,
  WEEKS,
} from "../../data/pioneerCohortSchedule";

const STORAGE_KEY = "pioneer_schedule_course";
const VIEW_KEY = "pioneer_schedule_view";
const WAT = "Africa/Lagos";
const DAY_MS = 24 * 60 * 60 * 1000;

const TYPE_STYLES = {
  class: { label: "Live class", dot: "bg-primary", chip: "bg-orange-50 text-primary" },
  "office-hours": { label: "Office hours", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700" },
  kickoff: { label: "Kickoff & orientation", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700" },
  orientation: { label: "Kickoff & orientation", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700" },
  webinar: { label: "Webinar", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700" },
  career: { label: "Career session", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  showcase: { label: "Showcase", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700" },
};
const LEGEND = ["class", "office-hours", "kickoff", "career", "showcase"];

// "2026-11-03" + "19:00" in WAT → Date
const watDate = (ymd, hm = "00:00") => new Date(`${ymd}T${hm}:00+01:00`);
const addDays = (ymd, n) => {
  const d = new Date(watDate(ymd).getTime() + n * DAY_MS);
  return d.toLocaleDateString("en-CA", { timeZone: WAT });
};

function buildCalendar() {
  const items = [];
  WEEKS.forEach((week) => {
    if (week.kind === "teaching") {
      Object.entries(CLASS_GROUPS).forEach(([group, cfg]) => {
        cfg.slots.forEach((slot) => {
          const date = addDays(week.monday, slot.day);
          items.push({ id: `class-${group}-${date}-${slot.start}`, type: "class", group, date, start: slot.start, end: slot.end, weekId: week.id });
        });
      });
    }
    if (week.kind === "teaching" || week.kind === "showcase") {
      const date = addDays(week.monday, OFFICE_HOURS.day);
      items.push({ id: `oh-${date}`, type: "office-hours", title: OFFICE_HOURS.title, date, start: OFFICE_HOURS.start, end: OFFICE_HOURS.end, weekId: week.id });
    }
  });
  EVENTS.forEach((e, i) => {
    const week = [...WEEKS].reverse().find((w) => w.monday <= e.date);
    items.push({ id: `ev-${i}`, ...e, weekId: week?.id });
  });
  return items.sort((a, b) => (a.date + (a.start || "")).localeCompare(b.date + (b.start || "")));
}

const CALENDAR = buildCalendar();

function eventTitle(item, course) {
  if (item.type !== "class") return item.title;
  if (course) return `${course.name} · live class`;
  return `${CLASS_GROUPS[item.group].label} courses · live class`;
}

function fmtTime(ymd, hm, tz) {
  if (!hm) return null;
  return watDate(ymd, hm).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz });
}

function fmtDay(ymd, tz) {
  return watDate(ymd, "12:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: tz });
}

// ── Add to calendar (.ics) ──
const icsStamp = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
function downloadIcs(items, course, filename) {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//E1X Academy//Pioneer Cohort//EN", "CALSCALE:GREGORIAN"];
  items.forEach((item) => {
    lines.push("BEGIN:VEVENT", `UID:${item.id}@e1xacademy.net`, `DTSTAMP:${icsStamp(new Date())}`);
    if (item.start) {
      lines.push(`DTSTART:${icsStamp(watDate(item.date, item.start))}`, `DTEND:${icsStamp(watDate(item.date, item.end || item.start))}`);
    } else {
      lines.push(`DTSTART;VALUE=DATE:${item.date.replace(/-/g, "")}`);
    }
    lines.push(`SUMMARY:${eventTitle(item, course).replace(/[,;]/g, "\\$&")}`, `DESCRIPTION:E1X ${COHORT_NAME}`, "END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function useNow(intervalMs) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function countdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${h}h ${m}m ${s % 60}s`;
}

// Beta-style table: Month | Week | one Date/Time pair per class group | cohort events.
function ScheduleTable({ weeks, course, tz, todayYmd }) {
  const groups = Object.entries(CLASS_GROUPS).filter(([g]) => !course || course.group === g);
  const cell = "border border-gray-200 px-3 py-2.5";
  const monthOf = (ymd) => watDate(ymd, "12:00").toLocaleDateString("en-GB", { month: "long", timeZone: WAT });
  const timeRange = (item) => (item.start ? `${fmtTime(item.date, item.start, tz)} – ${fmtTime(item.date, item.end, tz)}` : item.note);

  // One row per class slot (or per cohort event, whichever is more); months span their weeks.
  const rows = [];
  weeks.forEach((week) => {
    const perGroup = groups.map(([g]) => week.days.flatMap((d) => d.items).filter((i) => i.type === "class" && i.group === g));
    const others = week.days.flatMap((d) => d.items).filter((i) => i.type !== "class");
    const span = Math.max(1, ...perGroup.map((l) => l.length), others.length);
    for (let r = 0; r < span; r++) rows.push({ week, r, span, month: monthOf(week.monday), classes: perGroup.map((l) => l[r] || null), other: others[r] || null });
  });
  rows.forEach((row, i) => {
    row.monthStart = i === 0 || rows[i - 1].month !== row.month;
    row.monthSpan = row.monthStart ? rows.slice(i).findIndex((x) => x.month !== row.month) : 0;
    if (row.monthStart && row.monthSpan === -1) row.monthSpan = rows.length - i;
  });

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
      <table className="w-full min-w-[760px] border-collapse text-sm text-center">
        <thead className="bg-[#0b0f19] text-white">
          <tr>
            <th rowSpan={2} className="border border-white/10 px-3 py-3 font-bold w-24">Month</th>
            <th rowSpan={2} className="border border-white/10 px-3 py-3 font-bold w-28">Week</th>
            {groups.map(([g, cfg]) => (
              <th key={g} colSpan={2} className="border border-white/10 px-3 py-3 font-bold">
                {course ? course.name : `${cfg.label} courses`}
                {!course && (
                  <span className="block text-[11px] font-normal text-white/60 mt-0.5">
                    {COURSES.filter((c) => c.group === g).map((c) => c.name).join(" · ")}
                  </span>
                )}
              </th>
            ))}
            <th rowSpan={2} className="border border-white/10 px-3 py-3 font-bold">Cohort events (everyone)</th>
          </tr>
          <tr>
            {groups.map(([g]) => (
              <React.Fragment key={g}>
                <th className="border border-white/10 px-3 py-2 font-semibold text-white/80">Date</th>
                <th className="border border-white/10 px-3 py-2 font-semibold text-white/80">Time</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const { week } = row;
            const current = week.status === "current";
            const tint = current ? "bg-orange-50/70" : week.status === "past" ? "bg-gray-50 text-gray-400" : "";
            return (
              <tr key={i} className={tint}>
                {row.monthStart && <td rowSpan={row.monthSpan} className={`${cell} font-bold align-middle bg-white text-[#0b0f19]`}>{row.month}</td>}
                {row.r === 0 && (
                  <td rowSpan={row.span} className={`${cell} font-semibold align-middle whitespace-nowrap ${current ? "text-primary" : "text-[#0b0f19]"}`}>
                    {week.label}
                    {current && <span className="block text-[10px] font-bold uppercase tracking-wider">This week</span>}
                  </td>
                )}
                {week.kind === "break" ? (
                  row.r === 0 && (
                    <td rowSpan={row.span} colSpan={groups.length * 2} className={`${cell} font-bold uppercase tracking-[0.2em] text-gray-600 bg-[repeating-linear-gradient(-45deg,#fff,#fff_10px,#f4f6f9_10px,#f4f6f9_20px)]`}>
                      Break
                      <span className="block text-[11px] font-normal normal-case tracking-normal text-gray-500 mt-1">{week.note || "No classes"}</span>
                    </td>
                  )
                ) : (
                  row.classes.map((item, g) =>
                    item ? (
                      <React.Fragment key={g}>
                        <td className={`${cell} whitespace-nowrap font-medium ${item.date === todayYmd ? "text-primary" : ""}`}>
                          {fmtDay(item.date, WAT)}
                          {DAY_NOTES[item.date] && <span className="block text-[11px] font-normal text-amber-700">{DAY_NOTES[item.date]}</span>}
                        </td>
                        <td className={`${cell} whitespace-nowrap tabular-nums`}>{timeRange(item)}</td>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={g}>
                        <td className={cell} />
                        <td className={cell} />
                      </React.Fragment>
                    )
                  )
                )}
                <td className={`${cell} text-left`}>
                  {row.other && (
                    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={`h-2 w-2 rounded-full ${TYPE_STYLES[row.other.type].dot}`} />
                      <span className="font-medium text-[#0b0f19]">{row.other.title}</span>
                      <span className="text-gray-500 whitespace-nowrap">{fmtDay(row.other.date, WAT)} · {timeRange(row.other)}</span>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PioneerCohortSchedule() {
  const [params, setParams] = useSearchParams();
  const [courseSlug, setCourseSlug] = useState(() => params.get("course") || safeGet(STORAGE_KEY) || "all");
  const [tzMode, setTzMode] = useState("wat");
  const [showPast, setShowPast] = useState(false);
  const [view, setView] = useState(() => safeGet(VIEW_KEY) || "table");
  const now = useNow(1000);

  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = tzMode === "wat" ? WAT : localTz;
  const course = COURSES.find((c) => c.slug === courseSlug) || null;

  useEffect(() => {
    safeSet(STORAGE_KEY, courseSlug);
    const next = new URLSearchParams(params);
    if (courseSlug === "all") next.delete("course");
    else next.set("course", courseSlug);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug]);

  const visible = useMemo(
    () => CALENDAR.filter((item) => item.type !== "class" || !course || item.group === course.group),
    [course]
  );

  const todayYmd = now.toLocaleDateString("en-CA", { timeZone: WAT });
  const nextUp = visible.find((item) => watDate(item.date, item.end || item.start || "23:59") > now);
  const nextStart = nextUp ? watDate(nextUp.date, nextUp.start || "00:00") : null;
  const isLiveNow = nextStart && nextStart <= now;

  const weeks = WEEKS.map((week) => {
    const sunday = addDays(week.monday, 6);
    const status = sunday < todayYmd ? "past" : week.monday <= todayYmd ? "current" : "upcoming";
    const items = visible.filter((i) => i.weekId === week.id);
    const days = [];
    items.forEach((item) => {
      let day = days.find((d) => d.date === item.date);
      if (!day) days.push((day = { date: item.date, items: [] }));
      day.items.push(item);
    });
    Object.keys(DAY_NOTES).forEach((date) => {
      if (date >= week.monday && date <= sunday && !days.find((d) => d.date === date)) days.push({ date, items: [] });
    });
    days.sort((a, b) => a.date.localeCompare(b.date));
    return { ...week, sunday, status, days };
  });
  const pastCount = weeks.filter((w) => w.status === "past").length;
  const shownWeeks = showPast ? weeks : weeks.filter((w) => w.status !== "past");
  const teachingWeeks = WEEKS.filter((w) => w.kind === "teaching").length;
  const firstClass = CALENDAR.find((i) => i.type === "class");

  const jumpToCurrent = () => document.getElementById("week-current")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <MainLayouts>
      <div className="bg-[#f4f6f9] min-h-screen overflow-x-clip">
        {/* Hero */}
        <section className="relative bg-[#0b0f19] overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-primary rounded-full blur-[160px] opacity-15" />
          <div className="relative z-10 max-w-[1200px] mx-auto px-5 grid gap-10 lg:grid-cols-[1.3fr_1fr] items-end">
            <div>
              <div className="text-primary text-[12px] font-bold tracking-[0.25em] uppercase mb-5">{COHORT_NAME}</div>
              <h1 className="text-white text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
                Your cohort calendar<span className="text-primary">.</span>
              </h1>
              <p className="text-gray-400 text-lg mt-5 max-w-xl">
                Every live class, orientation, office hour and career session in one place. Pick your course to see your own schedule, then add it to your calendar.
              </p>
              <div className="flex flex-wrap gap-2 mt-7 text-sm">
                <span className="rounded-full bg-white/10 text-white px-4 py-1.5">{COURSES.length} courses</span>
                <span className="rounded-full bg-white/10 text-white px-4 py-1.5">{teachingWeeks} teaching weeks + showcase</span>
                {firstClass && <span className="rounded-full bg-white/10 text-white px-4 py-1.5">Classes start {fmtDay(firstClass.date, WAT)}</span>}
              </div>
            </div>

            {nextUp && (
              <div className="rounded-2xl bg-gradient-to-br from-primary to-[#e8793f] p-6 text-white shadow-xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
                  <span className={`h-2 w-2 rounded-full bg-white ${isLiveNow ? "animate-pulse" : ""}`} />
                  {isLiveNow ? "Happening now" : "Next up"}
                </div>
                <h2 className="mt-3 text-xl font-bold leading-snug">{eventTitle(nextUp, course)}</h2>
                <p className="mt-1 text-white/85 text-sm">
                  {fmtDay(nextUp.date, tz)}
                  {nextUp.start ? ` · ${fmtTime(nextUp.date, nextUp.start, tz)} – ${fmtTime(nextUp.date, nextUp.end, tz)}` : ` · ${nextUp.note || "time to be announced"}`}
                </p>
                {!isLiveNow && nextStart && <p className="mt-4 text-3xl font-bold tabular-nums">{countdown(nextStart - now)}</p>}
                <button
                  type="button"
                  onClick={() => downloadIcs([nextUp], course, "e1x-next-event.ics")}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-primary px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  <FiCalendar /> Add to calendar
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Controls */}
        <div className="sticky top-[72px] z-20 bg-[#f4f6f9]/95 backdrop-blur border-b border-gray-200">
          <div className="max-w-[1200px] mx-auto px-5 py-4 flex flex-wrap items-center gap-3">
            <label className="relative flex-1 min-w-[220px] max-w-md">
              <span className="sr-only">Your course</span>
              <select
                id="pioneer-course"
                value={courseSlug}
                onChange={(e) => setCourseSlug(e.target.value)}
                className="w-full appearance-none rounded-xl border border-gray-300 bg-white pl-4 pr-10 py-3 text-[15px] font-semibold text-[#0b0f19] focus:border-primary focus:outline-none"
              >
                <option value="all">All courses</option>
                {Object.entries(CLASS_GROUPS).map(([group, cfg]) => (
                  <optgroup key={group} label={`${cfg.label} classes`}>
                    {COURSES.filter((c) => c.group === group).map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500" />
            </label>

            <div className="inline-flex rounded-xl border border-gray-300 bg-white p-1" role="group" aria-label="Time zone">
              {[["wat", "WAT"], ["local", "My time"]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={tzMode === key}
                  onClick={() => setTzMode(key)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${tzMode === key ? "bg-[#0b0f19] text-white" : "text-gray-600"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="inline-flex rounded-xl border border-gray-300 bg-white p-1" role="group" aria-label="View">
              {[["table", "Table"], ["timeline", "Timeline"]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={view === key}
                  onClick={() => { setView(key); safeSet(VIEW_KEY, key); }}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${view === key ? "bg-[#0b0f19] text-white" : "text-gray-600"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => downloadIcs(visible.filter((i) => i.start), course, `e1x-pioneer-${course ? course.slug : "all"}.ics`)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-white hover:bg-[#b0451e]"
            >
              <FiDownload /> {course ? "Add my schedule to calendar" : "Add all to calendar"}
            </button>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-5 py-10 grid gap-6">
          {/* Context line + legend */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-gray-600 text-sm flex items-center gap-2">
              <FiGlobe className="text-gray-400" />
              {tzMode === "wat" ? "Times in WAT (West Africa Time)." : `Times in your time zone (${localTz.replace(/_/g, " ")}).`}
              {course && ` ${course.name} classes run ${CLASS_GROUPS[course.group].label}.`}
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-600">
              {LEGEND.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-full ${TYPE_STYLES[t].dot}`} />
                  {TYPE_STYLES[t].label}
                </span>
              ))}
            </div>
          </div>

          {!course && (
            <div className="grid gap-3 md:grid-cols-2">
              {Object.entries(CLASS_GROUPS).map(([group, cfg]) => (
                <div key={group} className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    {cfg.label} · {cfg.slots.map((s) => `${fmtTime("2026-11-03", s.start, WAT)}–${fmtTime("2026-11-03", s.end, WAT)}`).join(" / ")} WAT
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {COURSES.filter((c) => c.group === group).map((c) => (
                      <button key={c.slug} type="button" onClick={() => setCourseSlug(c.slug)} className="rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-[#0b0f19] hover:border-primary hover:text-primary">
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {pastCount > 0 && (
              <button type="button" onClick={() => setShowPast(!showPast)} className="text-sm font-semibold text-gray-600 hover:text-primary">
                {showPast ? "Hide" : "Show"} {pastCount} past week{pastCount === 1 ? "" : "s"}
              </button>
            )}
            {view === "timeline" && <button type="button" onClick={jumpToCurrent} className="text-sm font-semibold text-primary">Jump to this week ↓</button>}
          </div>

          {view === "table" && <ScheduleTable weeks={shownWeeks} course={course} tz={tz} todayYmd={todayYmd} />}

          {/* Timeline */}
          <div className="grid gap-4" hidden={view !== "timeline"}>
            {shownWeeks.map((week) => {
              const isBreak = week.kind === "break";
              const current = week.status === "current";
              return (
                <section
                  key={week.id}
                  id={current ? "week-current" : undefined}
                  className={`scroll-mt-40 rounded-2xl border bg-white overflow-hidden ${current ? "border-primary ring-4 ring-orange-100" : "border-gray-200"} ${week.status === "past" ? "opacity-60" : ""}`}
                >
                  <header
                    className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${isBreak ? "bg-[repeating-linear-gradient(-45deg,#fff,#fff_10px,#f4f6f9_10px,#f4f6f9_20px)]" : week.kind === "showcase" ? "bg-amber-50" : week.kind === "pre" ? "bg-violet-50/60" : ""}`}
                  >
                    <div>
                      <h3 className="text-lg font-bold text-[#0b0f19]">{week.label}</h3>
                      <p className="text-sm text-gray-500">
                        {fmtDay(week.monday, WAT)} – {fmtDay(week.sunday, WAT)}
                        {week.note ? ` · ${week.note}` : ""}
                      </p>
                    </div>
                    {current && <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">This week</span>}
                    {isBreak && !current && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-600">Break</span>}
                  </header>

                  {week.days.length > 0 && (
                    <ul className="divide-y divide-gray-100 border-t border-gray-100">
                      {week.days.map((day) => (
                        <li key={day.date} className={`grid gap-2 px-5 py-3 sm:grid-cols-[130px_1fr] ${day.date === todayYmd ? "bg-orange-50/50" : ""}`}>
                          <div className="text-sm font-semibold text-[#0b0f19]">
                            {fmtDay(day.date, WAT)}
                            {day.date === todayYmd && <span className="ml-2 text-xs font-bold text-primary">Today</span>}
                            {DAY_NOTES[day.date] && <span className="block text-xs font-medium text-amber-700">{DAY_NOTES[day.date]}</span>}
                          </div>
                          <div className="grid gap-2">
                            {day.items.map((item) => {
                              const style = TYPE_STYLES[item.type];
                              return (
                                <div key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${style.dot}`} />
                                  <span className="min-w-0 flex-1 text-[15px] font-medium text-[#0b0f19]">{eventTitle(item, course)}</span>
                                  <span className="inline-flex items-center gap-1 text-sm text-gray-600 tabular-nums">
                                    <FiClock className="text-gray-400" />
                                    {item.start ? `${fmtTime(item.date, item.start, tz)} – ${fmtTime(item.date, item.end, tz)}` : item.note}
                                  </span>
                                  {item.start && (
                                    <button
                                      type="button"
                                      onClick={() => downloadIcs([item], course, "e1x-event.ics")}
                                      className="rounded-lg p-1.5 text-gray-400 hover:bg-orange-50 hover:text-primary"
                                      aria-label={`Add ${eventTitle(item, course)} to calendar`}
                                    >
                                      <FiCalendar />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {day.items.length === 0 && <span className="text-sm text-gray-400">No sessions</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </MainLayouts>
  );
}
